import axios from "axios";
import Cookies from "js-cookie";
import { FIRMFLOWURL } from "./url";

const hasValueForRequest = (value) =>
    value !== null && value !== undefined && value !== "";

const normalizeStatusFilter = (status) =>
    hasValueForRequest(status) ? status : null;

const ASSIGNMENTS_REQUEST_TIMEOUT = 15000;
const inFlightAssignmentRequests = new Map();

const getAssignmentRequest = (key, request) => {
    if (inFlightAssignmentRequests.has(key)) {
        return inFlightAssignmentRequests.get(key);
    }

    const promise = request().finally(() => {
        inFlightAssignmentRequests.delete(key);
    });
    inFlightAssignmentRequests.set(key, promise);
    return promise;
};

const handleAssignmentRequestError = (err) => {
    const status = err.response?.status;
    if (status === 401) {
        Cookies.remove("Bearer");
        window.location.replace("/");
    }

    return {
        success: false,
        status,
        message: err.response?.data?.message || err.message || "Request failed.",
    };
};

export const getClientsSubscriptionStates = (id, status, includeInactive = false) => {
    return new Promise(async (resolve) => {
        try {
            const token = Cookies.get("Bearer");
            const response = await axios.get(
                `${FIRMFLOWURL}api/Analytics/GetClientsSubscriptionStates`,
                {
                    headers: { Authorization: token },
                    params: { serviceId: id, status, includeInactive: Boolean(includeInactive) },
                }
            );
            resolve({ ...response, success: true });
        } catch (err) {
            console.log(err);
            if (err.message.includes(401)) {
                Cookies.remove("Bearer");
                window.location.replace("/");
                return;
            }
            resolve({ success: false, message: err.message });
        }
    });
};

export const getMyAssignmentsReport = (includeInactive = false) => {
    return getAssignmentRequest(`report:${Boolean(includeInactive)}`, async () => {
        try {
            const token = Cookies.get("Bearer");
            const response = await axios.get(
                `${FIRMFLOWURL}api/Analytics/MyAssignmentsReport`,
                {
                    headers: { Authorization: token },
                    params: { includeInactive: Boolean(includeInactive) },
                    timeout: ASSIGNMENTS_REQUEST_TIMEOUT,
                }
            );
            return { ...response, success: true };
        } catch (err) {
            return handleAssignmentRequestError(err);
        }
    });
};

export const getUserJobs = (includeInactive = false) => {
    return getAssignmentRequest(`jobs:${Boolean(includeInactive)}`, async () => {
        try {
            const token = Cookies.get("Bearer");
            const response = await axios.get(
                `${FIRMFLOWURL}api/Analytics/UserJobs`,
                {
                    headers: { Authorization: token },
                    params: { includeInactive: Boolean(includeInactive) },
                    timeout: ASSIGNMENTS_REQUEST_TIMEOUT,
                }
            );
            return { ...response, success: true };
        } catch (err) {
            return handleAssignmentRequestError(err);
        }
    });
};

export const getUserServices = (includeInactive = false) => {
    return getAssignmentRequest(`services:${Boolean(includeInactive)}`, async () => {
        try {
            const token = Cookies.get("Bearer");
            const response = await axios.get(
                `${FIRMFLOWURL}api/Analytics/UserServices`,
                {
                    headers: { Authorization: token },
                    params: { includeInactive: Boolean(includeInactive) },
                    timeout: ASSIGNMENTS_REQUEST_TIMEOUT,
                }
            );
            return { ...response, success: true };
        } catch (err) {
            return handleAssignmentRequestError(err);
        }
    });
};

export const getUserChecklistItems = () => {
    return new Promise(async (resolve) => {
        try {
            const token = Cookies.get("Bearer");
            const response = await axios.get(
                `${FIRMFLOWURL}api/Analytics/UserChecklistItems`,
                {
                    headers: { Authorization: token },
                }
            );
            resolve({ ...response, success: true });
        } catch (err) {
            console.log(err);
            if (err.message.includes(401)) {
                Cookies.remove("Bearer");
                window.location.replace("/");
                return;
            }
            resolve({ success: false, message: err.message });
        }
    });
};

export const getFirmStatistics = () => {
    return new Promise(async (resolve) => {
        try {
            const token = Cookies.get("Bearer");
            const response = await axios.get(`${FIRMFLOWURL}api/Analytics/Firm`, {
                headers: { Authorization: token },
            });
            resolve({ ...response, success: true });
        } catch (err) {
            console.log(err);
            if (err.message.includes(401)) {
                Cookies.remove("Bearer");
                window.location.replace("/");
                return;
            }
            resolve({ success: false, message: err.message });
        }
    });
};
export const getRegisteredClients = () => {
    return new Promise(async (resolve) => {
        try {
            const token = Cookies.get("Bearer");
            const response = await axios.get(`${FIRMFLOWURL}api/Analytics/clients`, {
                headers: { Authorization: token },
            });
            resolve({ ...response, success: true });
        } catch (err) {
            console.log(err);
            if (err.message.includes(401)) {
                Cookies.remove("Bearer");
                window.location.replace("/");
                return;
            }
            resolve({ success: false, message: err.message });
        }
    });
};

export const getFirmAccountability = (data) => {
    return new Promise(async (resolve) => {
        try {
            const token = Cookies.get("Bearer");
            const response = await axios.post(
                `${FIRMFLOWURL}api/Analytics/Accountability`,
                {
                    ...data,
                    status: normalizeStatusFilter(data.status),
                },
                {
                    headers: { Authorization: token },
                    params: { includeInactive: Boolean(data.includeInactive) },
                }
            );
            resolve({ ...response, success: true });
        } catch (err) {
            console.log(err);
            if (err.message.includes(401)) {
                Cookies.remove("Bearer");
                window.location.replace("/");
                return;
            }
            resolve({ success: false, message: err.message });
        }
    });
};

export const getServiceAccountability = (data) => {
    return new Promise(async (resolve) => {
        try {
            const token = Cookies.get("Bearer");
            const queryParams = new URLSearchParams({
                startDate: data.startDate,
                endDate: data.endDate,
                employeeId: data.employeeId,
                serviceId: data.serviceId,
                departmentId: data.departmentId,
                includeInactive: Boolean(data.includeInactive),
            });

            if (hasValueForRequest(data.status)) {
                queryParams.set("status", data.status);
            }

            const response = await axios.get(
                `${FIRMFLOWURL}api/Analytics/ServiceAccountability?${queryParams.toString()}`,
                {
                    headers: { Authorization: token },
                }
            );
            resolve({ ...response, success: true });
        } catch (err) {
            console.log(err);
            if (err.message.includes(401)) {
                Cookies.remove("Bearer");
                window.location.replace("/");
                return;
            }
            resolve({ success: false, message: err.message });
        }
    });
};
